import React, { useState, useEffect } from 'react';
import { Upload, CheckCircle, XCircle, FileText, AlertCircle } from 'lucide-react';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';

const ImportModal = ({ isOpen, onClose, onImport }) => {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    if (isOpen) {
      fetchAccounts();
      fetchUsers();
      resetForm();
    }
  }, [isOpen]);

  const fetchAccounts = async () => {
    try {
      console.log('🔍 Fetching treasury accounts for import...');
      const response = await authFetch(`${API_BASE_URL}/api/treasury/accounts`);
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Treasury accounts fetched:', data.length, 'accounts');
        console.log('📊 Accounts data:', data);
        setAccounts(data);
      } else {
        console.error('❌ Accounts fetch error:', response.status, response.statusText);
        setAccounts([]);
      }
    } catch (error) {
      console.error('❌ Error fetching accounts:', error);
      setAccounts([]);
    }
  };

  const fetchUsers = async () => {
    try {
      console.log('🔍 Fetching users for import...');
      const response = await authFetch(`${API_BASE_URL}/api/users-simple/options`);
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Users fetched:', data.length, 'users');
        console.log('📊 Users data:', data);
        setUsers(data);
      } else {
        console.error('❌ Users fetch error:', response.status, response.statusText);
        setUsers([]);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setUsers([]);
    }
  };

  const resetForm = () => {
    setSelectedAccount('');
    setSelectedUser('');
    setSelectedFile(null);
    setImportProgress(0);
    setImportResult(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
  };

  const handleImport = async () => {
    if (!selectedAccount || !selectedUser || !selectedFile) {
      return;
    }

    setIsImporting(true);
    setImportProgress(10);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('bank_account_id', selectedAccount);
    formData.append('imported_by', selectedUser);

    try {
      setImportProgress(30);
      
      const response = await authFetch(`${API_BASE_URL}/api/treasury/import-transactions`, {
        method: 'POST',
        body: formData,
      });

      setImportProgress(80);

      if (response.ok) {
        const result = await response.json();
        setImportResult({
          success: true,
          ...result
        });
        setImportProgress(100);
        
        // Llamar callback si la importación fue exitosa
        if (result.success && onImport) {
          setTimeout(() => {
            onImport(result);
            onClose();
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        setImportResult({
          success: false,
          error: errorData.detail || 'Error en la importación'
        });
      }
    } catch (error) {
      setImportResult({
        success: false,
        error: `Error de conexión: ${error.message}`
      });
    } finally {
      setIsImporting(false);
      setImportProgress(100);
    }
  };

  if (!isOpen) return null;

  const modalStyles = {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000'
  };

  const contentStyles = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const headerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #e2e8f0'
  };

  const inputStyles = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    marginBottom: '16px'
  };

  const buttonStyles = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.2s'
  };

  const primaryButtonStyles = {
    ...buttonStyles,
    backgroundColor: '#3182ce',
    color: 'white'
  };

  const secondaryButtonStyles = {
    ...buttonStyles,
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db'
  };

  const fileNameStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    border: '2px dashed #48bb78',
    borderRadius: '8px',
    backgroundColor: '#f0fff4'
  };

  return (
    <>
      <div style={modalStyles}>
        <div style={contentStyles}>
        <div style={headerStyles}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Upload size={24} color="#3182CE" />
            <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#3182CE' }}>
              Importar Transacciones
            </span>
          </div>
          <button 
            style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}
            onClick={onClose}
            disabled={isImporting}
          >
            ✕
          </button>
        </div>

          {/* Configuración de importación */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Debug info */}
            {console.log('🔍 ImportModal render - Accounts:', accounts.length, 'Users:', users.length)}
            
            {/* Cuenta bancaria */}
            <div>
              <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                Cuenta Bancaria *
              </label>
              <select
                style={inputStyles}
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                disabled={isImporting}
              >
                <option value="">Selecciona una cuenta bancaria</option>
                {accounts.length === 0 ? (
                  <option value="" disabled>⚠️ No hay cuentas disponibles - Cargando...</option>
                ) : (
                  accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.account_name} ({account.bank_name})
                    </option>
                  ))
                )}
              </select>
              {accounts.length > 0 && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {accounts.length} cuenta{accounts.length !== 1 ? 's' : ''} disponible{accounts.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Usuario que importa */}
            <div>
              <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                Importado por *
              </label>
              <select
                style={inputStyles}
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                disabled={isImporting}
              >
                <option value="">Selecciona el usuario</option>
                {users.length === 0 ? (
                  <option value="" disabled>⚠️ No hay usuarios disponibles - Cargando...</option>
                ) : (
                  users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.username})
                    </option>
                  ))
                )}
              </select>
              {users.length > 0 && (
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {users.length} usuario{users.length !== 1 ? 's' : ''} disponible{users.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            {/* Archivo */}
            <div>
              <label style={{ fontWeight: '500', marginBottom: '8px', display: 'block' }}>
                Archivo de Cartola Bancaria *
              </label>
              <div
                style={{
                  ...fileNameStyles,
                  border: selectedFile ? "2px dashed #48BB78" : "2px dashed #CBD5E0",
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => document.getElementById('fileInput').click()}
                onMouseEnter={(e) => {
                  if (!selectedFile) {
                    e.target.style.borderColor = "#3182CE";
                    e.target.style.backgroundColor = "#F7FAFC";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!selectedFile) {
                    e.target.style.borderColor = "#CBD5E0";
                    e.target.style.backgroundColor = fileNameStyles.backgroundColor;
                  }
                }}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  disabled={isImporting}
                />
                {selectedFile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <FileText size={48} color="#48BB78" />
                    <span style={{ color: '#48BB78', fontWeight: '500' }}>
                      {selectedFile.name}
                    </span>
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <Upload size={48} color="#718096" />
                    <span style={{ color: '#666' }}>
                      Haz clic para seleccionar archivo
                    </span>
                    <span style={{ fontSize: '14px', color: '#888' }}>
                      Formatos soportados: .xls, .xlsx
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Progreso de importación */}
          {isImporting && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '16px', height: '16px', border: '2px solid #3182CE', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                <span style={{ fontSize: '14px', color: '#3182CE' }}>
                  Procesando archivo...
                </span>
              </div>
              <div style={{ 
                width: '100%', 
                height: '8px', 
                backgroundColor: '#e2e8f0', 
                borderRadius: '4px', 
                overflow: 'hidden' 
              }}>
                <div 
                  style={{
                    width: `${importProgress}%`,
                    height: '100%',
                    backgroundColor: '#3182CE',
                    transition: 'width 0.3s ease'
                  }}
                ></div>
              </div>
            </div>
          )}

          {/* Resultado de la importación */}
          {importResult && (
            <div style={{
              padding: '16px',
              borderRadius: '6px',
              backgroundColor: importResult.success ? '#f0fff4' : '#fef2f2',
              border: `1px solid ${importResult.success ? '#48bb78' : '#f56565'}`,
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {importResult.success ? (
                  <CheckCircle size={20} color="#48bb78" />
                ) : (
                  <AlertCircle size={20} color="#f56565" />
                )}
                <span style={{ 
                  fontWeight: '600', 
                  color: importResult.success ? '#48bb78' : '#f56565' 
                }}>
                  {importResult.success ? "¡Importación exitosa!" : "Error en la importación"}
                </span>
              </div>
              <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                {importResult.success ? (
                  <>
                    {importResult.metadata && (
                      <>
                        <div style={{ marginBottom: '4px' }}>
                          🏦 Cuenta: {importResult.metadata.bank_account_id}
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          📊 Transacciones: {importResult.metadata.imported_rows} importadas
                        </div>
                        {importResult.metadata.skipped_rows > 0 && (
                          <div style={{ marginBottom: '4px' }}>
                            ⚠️ Duplicados: {importResult.metadata.skipped_rows} omitidos
                          </div>
                        )}
                        <div style={{ marginBottom: '4px' }}>
                          📄 Archivo: {importResult.metadata.filename}
                        </div>
                      </>
                    )}
                    {importResult.message}
                  </>
                ) : (
                  importResult.error || "Ocurrió un error inesperado"
                )}
              </div>
            </div>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button
              style={{ ...secondaryButtonStyles, flex: '1' }}
              onClick={onClose}
              disabled={isImporting}
            >
              Cancelar
            </button>
            <button
              style={{ 
                ...primaryButtonStyles, 
                flex: '2', 
                opacity: (!selectedAccount || !selectedUser || !selectedFile || isImporting) ? 0.5 : 1,
                cursor: (!selectedAccount || !selectedUser || !selectedFile || isImporting) ? 'not-allowed' : 'pointer'
              }}
              onClick={handleImport}
              disabled={!selectedAccount || !selectedUser || !selectedFile || isImporting}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                <Upload size={16} />
                {isImporting ? 'Importando...' : 'Importar'}
              </div>
            </button>
          </div>
        </div>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default ImportModal;
